import { State } from '@meraki-internal/state';
import { ICaremapNote, ICaremapNoteResponse } from '../model/NotesClient';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { IFreeTierLimits } from '../../innerhive-plus/revenue-cat/IFreeTierLimits';

export class NewNoteFormViewModel extends State<Record<string, never>> {
    constructor(private note: ICaremapNote, private factory: CaremapNotesModelFactory){
        super({ });
    }

    isValid = () => this.factory.validator.isValid(this.getNote());

    getNote = () => {
        return this.note;
    };

    setNote = (note: ICaremapNote) => {
        this.note = note;
        this.setState({ });
    };

    private isSaving = false;

    private didSave = false;

    canSave = () => {
        // cannot save again
        if (this.didSave){
            return false;
        }

        return !this.isSaving && this.isValid();
    };

    addAttachmentRequiresInnerhivePlus = (limits: IFreeTierLimits) => {
        return this.factory.attachmentCountHelper.addAttachmentRequiresInnerhivePlus(limits, this.getNote());
    };

    // TODO: at some point maybe consolidate tracking somehow
    save = async ({
        trackingEvent,
        trackingFrom,
        trackingTemplateLabel,
        toastMsg,
        skipToast
    }: {
        trackingEvent?: string;
        trackingFrom?: string;
        trackingTemplateLabel?: string;
        toastMsg?: string;
        skipToast?: boolean;
    } = {}) => {
        this.isSaving = true;
        this.setState({ });

        try {
            const response =  await this.factory.forActiveCaremap().addNewNote(this.getNote());

            this.didSave = true;
            this.setState({ });

            this.trackSaved(response, trackingEvent, trackingFrom, trackingTemplateLabel);

            if (!skipToast) {
                this.maybeToast(trackingEvent, toastMsg);
            }

            return response;

        }
        finally {
            this.isSaving = false;
            this.setState({ });
        }
    };

    private isOnNotesPageOrCaremapDrawer = () => {
        const { pathname } = this.factory.history.getCurrentLocation();
        if (pathname.startsWith('/notes')){
            return true;
        }

        if (pathname.startsWith('/caremaps') && pathname.includes('drawer')){
            return true;
        }

        return false;
    };

    // really this should just be the feed?
    private isOnCommunity = () => {
        const { pathname } = this.factory.history.getCurrentLocation();
        return pathname.startsWith('/community/');
    };

    // if we're not already on the right page, show a toast
    private maybeToast = async (trackingEvent?: string, toastMsg?: string) => {

        // TODO maybe figure this out in a less hacky way?
        const isCommunityPost = (trackingEvent && trackingEvent.startsWith('Community'));

        const activeCaremap = this.factory.careMapState.getActiveCaremap();
        if (activeCaremap){

            if (!isCommunityPost && !this.isOnNotesPageOrCaremapDrawer()){

                // TODO: animation pause that is test aware
                await new Promise(resolve => setTimeout(resolve, 350));

                this.factory.toastPresenter.toast({
                    duration: 3000,
                    message: toastMsg || 'Note Added!',
                    buttons: [{
                        text: 'View Notes',
                        handler: () => {
                            this.factory.history.push(`/notes/${activeCaremap.careMapId!}`);
                        },
                    }]
                });
            }

            if (isCommunityPost && !this.isOnCommunity()) {

                // TODO: animation pause that is test aware
                await new Promise(resolve => setTimeout(resolve, 350));

                this.factory.toastPresenter.toast({
                    duration: 3000,
                    message: 'Post Added!',
                    buttons: [{
                        text: 'View Community Posts',
                        handler: () => {
                            this.factory.history.push(`/community/${activeCaremap.careMapId!}/feed`);
                        },
                    }]
                });
            }
        }
    };

    private trackSaved = (
        savedNote: ICaremapNoteResponse,
        trackingEvent?: string,
        trackingFrom?: string,
        trackingTemplateLabel?: string
    ) => {
        this.factory.tracking.onAddedNote(savedNote, trackingEvent, trackingFrom, trackingTemplateLabel);
    };
}
